import axios from 'axios';
import useAuthStore from '@/store/authStore';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081';

// Variable pour stocker le token CSRF en mémoire (pour production où cookie est HttpOnly)
let csrfTokenCache = null;

// Fonction pour récupérer le token CSRF depuis les cookies (dev) ou le cache (prod)
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// Fonction pour récupérer le token CSRF (depuis cookie ou cache)
function getCsrfToken() {
  // Essayer d'abord depuis les cookies (dev)
  const cookieToken = getCookie('csrftoken');
  if (cookieToken) {
    return cookieToken;
  }
  // Sinon utiliser le cache (prod)
  return csrfTokenCache;
}

// Fonction pour récupérer le token CSRF depuis l'API
async function fetchCsrfToken() {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/csrf-token/`, {
      withCredentials: true,
    });
    const token = response.data.csrfToken || response.headers['x-csrftoken'];
    if (token) {
      csrfTokenCache = token;
      return token;
    }
  } catch (error) {
    console.warn('Erreur lors de la récupération du token CSRF:', error);
  }
  return null;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important pour envoyer les cookies de session
});

// Intercepteur pour ajouter le token CSRF et gérer FormData
api.interceptors.request.use(
  async (config) => {
    // Ignorer pour les requêtes GET et les endpoints qui ne nécessitent pas CSRF
    const needsCsrf = config.method && ['post', 'put', 'patch', 'delete'].includes(config.method.toLowerCase());
    const isCsrfEndpoint = config.url?.includes('/api/csrf-token/');

    if (needsCsrf && !isCsrfEndpoint) {
      let csrfToken = getCsrfToken();

      // Si pas de token, essayer de le récupérer depuis l'API
      if (!csrfToken) {
        csrfToken = await fetchCsrfToken();
      }

      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      }
    }

    const { accessToken } = useAuthStore.getState();
    if (accessToken && !isAuthEndpointUrl(config.url || '')) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Pour FormData, supprimer le Content-Type pour laisser le navigateur le définir avec le bon boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Flag pour éviter les redirections multiples
let isRedirecting = false;
let refreshPromise = null;

const AUTH_ENDPOINTS = [
  '/accounts/token/',
  '/accounts/token/refresh/',
  '/accounts/token/logout/',
  '/accounts/login/',
  '/accounts/register/',
  '/accounts/send-otp/',
  '/accounts/verify-otp/',
];

const isAuthEndpointUrl = (url = '') =>
  AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));

const refreshAccessToken = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = api.post('/accounts/token/refresh/', {});
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
};

// Fonction pour gérer la déconnexion due à une session expirée
const handleSessionExpired = () => {
  if (isRedirecting) {
    console.log('Redirection déjà en cours, ignorée');
    return; // Éviter les redirections multiples
  }

  console.log('Déclenchement de la déconnexion automatique...');
  isRedirecting = true;

  // Importer dynamiquement pour éviter les dépendances circulaires
  import('@/store/authStore').then(({ default: useAuthStore }) => {
    const { logout } = useAuthStore.getState();

    // Nettoyer le store d'authentification
    logout();

    // Stocker le message dans sessionStorage pour l'afficher après redirection
    sessionStorage.setItem('sessionExpiredMessage', 'true');

    // Rediriger vers la page de connexion
    // Utiliser window.location pour forcer un rechargement complet et nettoyer l'état
    setTimeout(() => {
      console.log('Redirection vers la page de login...');
      window.location.href = '/auth?tab=login&session_expired=true';
    }, 100);
  }).catch((err) => {
    console.error('Erreur lors de la déconnexion automatique:', err);
    // En cas d'erreur, réinitialiser le flag et forcer la redirection
    isRedirecting = false;
    window.location.href = '/auth?tab=login&session_expired=true';
  });
};

// Intercepteur pour gérer les erreurs HTTP
api.interceptors.response.use(
  (response) => {
    // Réinitialiser le flag si la requête réussit
    isRedirecting = false;
    return response;
  },
  async (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';

    // Extraire le message d'erreur de différents champs possibles
    const errorData = error.response?.data || {};
    const errorMessage = (
      errorData.detail ||
      errorData.message ||
      errorData.error ||
      (typeof errorData === 'string' ? errorData : '') ||
      ''
    ).toString().toLowerCase();

    // Détecter si l'erreur est liée au CSRF (ne pas déconnecter pour ça)
    const isCsrfError =
      errorMessage.includes('csrf') ||
      errorMessage.includes('csrf token') ||
      errorMessage.includes('csrf failed');

    // Détecter si l'erreur indique une session expirée (même pour 403)
    // Exclure les erreurs CSRF qui ne sont pas des sessions expirées
    const isSessionExpiredError =
      !isCsrfError && (
        errorMessage.includes('authentication credentials') ||
        errorMessage.includes('not authenticated') ||
        errorMessage.includes('session expired') ||
        errorMessage.includes('invalid session') ||
        errorMessage.includes('credentials were not provided') ||
        errorMessage.includes('authentication required')
      );

    // Gestion des erreurs 401 (non authentifié)
    if (status === 401) {
      const originalRequest = error.config;
      const isAuthEndpoint = isAuthEndpointUrl(url) || url.includes('/accounts/me/');

      if (!isAuthEndpoint && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const refreshResponse = await refreshAccessToken();
          const newAccessToken = refreshResponse?.data?.access;
          if (newAccessToken) {
            useAuthStore.getState().setAccessToken(newAccessToken);
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return api(originalRequest);
          }
        } catch (_refreshError) {
          // Si le refresh échoue, on déconnecte proprement ci-dessous.
        }
      }

      if (!isAuthEndpoint && !isRedirecting) {
        handleSessionExpired();
      }
    }

    // Gestion des erreurs 403 (accès refusé) - déconnecter si c'est une session expirée
    if (status === 403) {
      const isAuthEndpoint = isAuthEndpointUrl(url) || url.includes('/accounts/me/');

      // Si c'est une erreur CSRF, essayer de récupérer le token et réessayer
      if (isCsrfError && !isAuthEndpoint) {
        console.log('Erreur CSRF détectée, récupération du token...');
        // Réinitialiser le cache et réessayer une fois
        csrfTokenCache = null;
        const retryConfig = error.config;
        if (retryConfig && !retryConfig._retry) {
          retryConfig._retry = true;
          const newToken = await fetchCsrfToken();
          if (newToken) {
            retryConfig.headers['X-CSRFToken'] = newToken;
            return api(retryConfig);
          }
        }
        // Si on ne peut pas récupérer le token, c'est peut-être une session expirée
        console.warn('Impossible de récupérer le token CSRF, peut-être une session expirée');
      } else if (isSessionExpiredError && !isAuthEndpoint && !isRedirecting) {
        // Ne déconnecter que si c'est vraiment une session expirée (pas juste une erreur CSRF)
        // et seulement pour les requêtes qui ne sont pas des endpoints d'authentification
        console.log('Session expirée détectée via erreur 403:', errorMessage);
        // Gérer la session expirée
        handleSessionExpired();
      } else if (!isSessionExpiredError && !isCsrfError && !isAuthEndpoint) {
        // Pour les 403 qui ne sont pas des sessions expirées ni CSRF, juste logger
        console.warn('Accès refusé (403) pour:', url, 'Message:', errorMessage);
      }
    }

    // Gestion des erreurs 500+ (erreurs serveur)
    // Rediriger vers la page d'erreur 500 pour les erreurs serveur critiques
    // mais seulement si ce n'est pas une requête qui peut être gérée localement
    if (status >= 500 && status < 600) {
      // Ne pas rediriger pour les endpoints qui peuvent gérer l'erreur localement
      // (par exemple, les formulaires qui affichent l'erreur dans le champ)
      const canHandleLocally = error.config?.skipErrorHandling === true;

      if (!canHandleLocally) {
        // Rediriger vers la page d'erreur 500
        console.log(`Erreur serveur ${status}, redirection vers la page d'erreur`);
        window.location.href = `/error/500`;
      }
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  // Récupérer le cookie CSRF avant le login
  getCsrfToken: async () => {
    // Essayer d'abord l'endpoint dédié (pour production)
    try {
      const response = await axios.get(`${API_BASE_URL}/api/csrf-token/`, {
        withCredentials: true,
      });
      const token = response.data.csrfToken || response.headers['x-csrftoken'];
      if (token) {
        csrfTokenCache = token;
        return response;
      }
    } catch (error) {
      console.warn('Erreur lors de la récupération du token CSRF depuis /api/csrf-token/, tentative avec /accounts/login/');
    }
    // Fallback sur l'endpoint login (pour dev)
    return api.get('/accounts/login/');
  },
  login: (credentials) => api.post('/accounts/token/', credentials),
  sendOtp: (data) => api.post('/accounts/send-otp/', data),
  verifyOtp: (data) => api.post('/accounts/verify-otp/', data),
  register: (userData) => api.post('/accounts/register/', userData),
  logout: () => api.post('/accounts/token/logout/'),
  refreshToken: () => api.post('/accounts/token/refresh/', {}),
  me: () => api.get('/accounts/me/'),
  updateProfile: (userData) => api.patch('/accounts/me/', userData),
  updateProfilePhoto: (formData) => api.patch('/accounts/me/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  changePassword: (passwordData) => api.post('/accounts/change-password/', passwordData),
  // Statistiques spécifiques par rôle
  getTransporterStatistics: () => api.get('/accounts/transporter/statistics/'),
  getClientStatistics: () => api.get('/accounts/client/statistics/'),
};

export const clientAPI = {
  // Demandes de transport
  createRequest: (requestData) => api.post('/transport/requests/', requestData),
  getRequests: (params = {}) => api.get('/transport/requests/', { params }),
  getRequestById: (id) => api.get(`/transport/requests/${id}/`),
  updateRequest: (id, requestData) => api.patch(`/transport/requests/${id}/`, requestData),
  deleteRequest: (id) => api.delete(`/transport/requests/${id}/`),
  publishRequest: (id) => api.patch(`/transport/requests/${id}/`, { status: 'PUBLISHED' }),
  unpublishRequest: (id) => api.patch(`/transport/requests/${id}/`, { status: 'DRAFT' }),
  cancelRequest: (id) => api.patch(`/transport/requests/${id}/`, { status: 'CANCELLED' }),

  // Offres/Quotes
  getQuotes: (requestId, params = {}) => {
    const queryParams = { ...params };
    if (requestId) {
      queryParams.request = requestId;
    }
    return api.get('/transport/offers/', { params: queryParams });
  },
  getQuoteById: (id) => api.get(`/transport/offers/${id}/`),
  acceptQuote: (offerId, finalAmount = null) => {
    const payload = { status: 'ACCEPTED' };
    if (finalAmount !== null) {
      payload.final_amount = finalAmount;
    }
    return api.patch(`/transport/offers/${offerId}/`, payload);
  },
  rejectQuote: (offerId) => api.patch(`/transport/offers/${offerId}/`, { status: 'REJECTED' }),

  // Suivi et tracking
  trackShipment: (requestId) => api.get(`/tracking/events/?request_id=${requestId}`),

  // Paiements
  getPayments: (params = {}) => api.get('/payments/payments/', { params }),
  getPaymentById: (id) => api.get(`/payments/payments/${id}/`),
  initiatePayment: (paymentId) => api.post(`/payments/payments/${paymentId}/initiate/`),

  // Factures
  getInvoices: (params = {}) => api.get('/payments/invoices/', { params }),
  getInvoiceById: (id) => api.get(`/payments/invoices/${id}/`),

  // Localisations
  createLocation: (locationData) => api.post('/transport/locations/', locationData),
  getLocations: (params = {}) => api.get('/transport/locations/', { params }),

  // Éléments de cargaison
  createCargoItem: (requestId, cargoData) => api.post('/transport/cargo-items/', { ...cargoData, request: requestId }),
  updateCargoItem: (id, cargoData) => api.patch(`/transport/cargo-items/${id}/`, cargoData),
  deleteCargoItem: (id) => api.delete(`/transport/cargo-items/${id}/`),

  // Réservations
  getBookings: (params = {}) => api.get('/transport/bookings/', { params }),
  getBookingById: (id) => api.get(`/transport/bookings/${id}/`),

  // Évaluations
  createRating: (ratingData) => api.post('/transport/ratings/', ratingData),
  getRatings: (params = {}) => api.get('/transport/ratings/', { params }),
};

// transporterAPI sera défini plus bas dans le fichier

export const adminAPI = {
  // User Management
  getUsers: (params) => api.get('/accounts/admin/users/', { params }),
  getUserById: (id) => api.get(`/accounts/admin/users/${id}/`),
  createUser: (userData) => api.post('/accounts/admin/users/', userData),
  updateUser: (id, userData) => api.patch(`/accounts/admin/users/${id}/`, userData),
  deleteUser: (id) => api.delete(`/accounts/admin/users/${id}/`),
  activateUser: (id) => api.post(`/accounts/admin/users/${id}/activate/`),
  deactivateUser: (id) => api.post(`/accounts/admin/users/${id}/deactivate/`),
  resetPassword: (id, newPassword) => api.post(`/accounts/admin/users/${id}/reset_password/`, { new_password: newPassword }),
  getUserStatistics: () => api.get('/accounts/admin/users/statistics/'),

  // Transporter Management
  getTransporters: (params) => api.get('/accounts/admin/users/transporters/', { params }),
  getTransporterById: (id) => api.get(`/accounts/admin/users/transporter_by_profile_id/?profile_id=${id}`),
  createTransporter: (transporterData) => api.post('/accounts/admin/users/', {
    ...transporterData,
    role: 'TRANSPORTEUR'
  }),
  updateTransporter: (id, transporterData) => api.patch(`/accounts/admin/users/${id}/`, transporterData),
  deleteTransporter: (id) => api.delete(`/accounts/admin/users/${id}/`),
  activateTransporter: (id) => api.post(`/accounts/admin/users/${id}/activate/`),
  deactivateTransporter: (id) => api.post(`/accounts/admin/users/${id}/deactivate/`),
  resetTransporterPassword: (id, newPassword) => api.post(`/accounts/admin/users/${id}/reset_password/`, { new_password: newPassword }),

  // Transporter-related entities (for admin view of transporter details)
  getTransporterOffers: (transporterId, params = {}) => {
    return api.get('/transport/offers/', { params: { ...params, transporter: transporterId } });
  },
  getTransporterBookings: (transporterId, params = {}) => {
    return api.get('/transport/bookings/', { params: { ...params, transporter: transporterId } });
  },
  getTransporterPayments: (transporterId, params = {}) => {
    return api.get('/payments/payments/', { params: { ...params, recipient_user__transporter_profile: transporterId } });
  },
  getTransporterInvoices: (transporterId, params = {}) => {
    return api.get('/payments/invoices/', { params: { ...params, transporter: transporterId } });
  },
  getTransporterRatings: (transporterId, params = {}) => {
    return api.get('/transport/ratings/', { params: { ...params, transporter: transporterId } });
  },

  // Client Management
  getClients: (params) => {
    // Utiliser l'endpoint spécifique pour les clients avec toutes les données
    return api.get('/accounts/admin/users/clients/', { params });
  },
  getClientById: (id) => api.get(`/accounts/admin/users/client_by_profile_id/?profile_id=${id}`),
  createClient: (clientData) => {
    // Séparer les champs utilisateur des champs de profil
    const {
      company_name,
      type_client,
      address,
      domaine_activites,
      ...userFields
    } = clientData;

    // Construire l'objet de données avec le profil séparé
    const payload = {
      ...userFields,
      role: 'CLIENT',
      profile: {
        company_name: company_name || '',
        type_client: type_client || 'INDIVIDUAL',
        address: address || '',
        domaine_activites: domaine_activites || [],
      }
    };

    return api.post('/accounts/admin/users/', payload);
  },
  updateClient: (id, clientData) => {
    // Séparer les champs utilisateur des champs de profil
    const {
      company_name,
      type_client,
      address,
      domaine_activites,
      ...userFields
    } = clientData;

    // Construire l'objet de données avec le profil séparé
    const payload = {
      ...userFields,
      profile: {
        company_name: company_name || '',
        type_client: type_client || 'INDIVIDUAL',
        address: address || '',
        domaine_activites: domaine_activites || [],
      }
    };

    return api.patch(`/accounts/admin/users/${id}/`, payload);
  },
  deleteClient: (id) => api.delete(`/accounts/admin/users/${id}/`),
  activateClient: (id) => api.post(`/accounts/admin/users/${id}/activate/`),
  deactivateClient: (id) => api.post(`/accounts/admin/users/${id}/deactivate/`),
  resetClientPassword: (id, newPassword) => api.post(`/accounts/admin/users/${id}/reset_password/`, { new_password: newPassword }),

  // Transport Requests and Offers (admin access)
  getRequestById: (id) => api.get(`/transport/requests/${id}/`),
  getOfferById: (id) => api.get(`/transport/offers/${id}/`),
  updateRequest: (id, data) => api.patch(`/transport/requests/${id}/`, data),
  updateOffer: (id, data) => api.patch(`/transport/offers/${id}/`, data),
  forceAcceptOffer: (id) => api.patch(`/transport/offers/${id}/`, { status: 'ACCEPTED' }),
  forceRejectOffer: (id) => api.patch(`/transport/offers/${id}/`, { status: 'REJECTED' }),
  cancelRequestAsAdmin: (id) => api.patch(`/transport/requests/${id}/`, { status: 'CANCELLED' }),

  // Client-related entities (for admin view of client details)
  getClientRequests: (clientId, params = {}) => {
    // Filtrer par client_id via le profil
    return api.get('/transport/requests/', { params: { ...params, client: clientId } });
  },
  createRequestForClient: (clientId, requestData) => {
    // Créer une demande au nom d'un client spécifique
    return api.post('/transport/requests/', { ...requestData, client: clientId });
  },
  getClientOffers: (clientId, params = {}) => {
    return api.get('/transport/offers/', { params: { ...params, request__client: clientId } });
  },
  getClientBookings: (clientId, params = {}) => {
    return api.get('/transport/bookings/', { params: { ...params, client: clientId } });
  },
  getClientPayments: (clientId, params = {}) => {
    return api.get('/payments/payments/', { params: { ...params, payer_user__client_profile: clientId } });
  },
  getClientInvoices: (clientId, params = {}) => {
    return api.get('/payments/invoices/', { params: { ...params, client: clientId } });
  },
  getClientRatings: (clientId, params = {}) => {
    return api.get('/transport/ratings/', { params: { ...params, client: clientId } });
  },

  // Other admin endpoints
  validateTransporter: (transporterId, status) => api.patch(`/admin/transporters/${transporterId}/validate/`, { status }),
  getStatistics: () => api.get('/analytics/admin/statistics/'),
  getChartData: (period = 'all') => api.get('/analytics/admin/chart-data/', { params: { period } }),
  getRecentActivities: (limit = 10) => api.get('/analytics/admin/recent-activities/', { params: { limit } }),
  getPayments: () => api.get('/admin/payments/'),

  // Admin Settings (singleton plateforme)
  getAdminSettings: () => api.get('/api/admin/settings/'),
  updateAdminSettings: (data) => api.patch('/api/admin/settings/', data),

  // Admin Testimonials
  getTestimonials: () => api.get('/api/admin/testimonials/'),
  createTestimonial: (data) => api.post('/api/admin/testimonials/', data),
  updateTestimonial: (id, data) => api.patch(`/api/admin/testimonials/${id}/`, data),
  deleteTestimonial: (id) => api.delete(`/api/admin/testimonials/${id}/`),

  // Admin Contact Messages
  getContactMessages: () => api.get('/api/admin/contact-messages/'),
  updateContactMessage: (id, data) => api.patch(`/api/admin/contact-messages/${id}/`, data),

  // Admin Operations - Requests
  getAllRequests: (params = {}) => api.get('/transport/admin/requests/', { params }),
  updateRequestStatus: (requestId, status) => api.post(`/transport/admin/requests/${requestId}/update_status/`, { status }),
  updateRequestVisibility: (requestId, data) => api.post(`/transport/admin/requests/${requestId}/update_visibility/`, data),
  deleteRequest: (requestId) => api.delete(`/transport/admin/requests/${requestId}/`),
  restoreRequest: (requestId) => api.post(`/transport/admin/requests/${requestId}/restore/`),

  // Admin Operations - Offers
  getAllOffers: (params = {}) => api.get('/transport/admin/offers/', { params }),
  acceptOffer: (offerId) => api.post(`/transport/admin/offers/${offerId}/accept/`),
  rejectOffer: (offerId) => api.post(`/transport/admin/offers/${offerId}/reject/`),
  updateOfferVisibility: (offerId, data) => api.post(`/transport/admin/offers/${offerId}/update_visibility/`, data),
  deleteOffer: (offerId) => api.delete(`/transport/admin/offers/${offerId}/`),
  restoreOffer: (offerId) => api.post(`/transport/admin/offers/${offerId}/restore/`),

  // Admin Operations - Bookings
  getAllBookings: (params = {}) => api.get('/transport/admin/bookings/', { params }),
  startMission: (bookingId) => api.post(`/transport/admin/bookings/${bookingId}/start_mission/`),
  deliverMission: (bookingId) => api.post(`/transport/admin/bookings/${bookingId}/deliver_mission/`),
  completeMission: (bookingId) => api.post(`/transport/admin/bookings/${bookingId}/complete_mission/`),
  deleteBooking: (bookingId) => api.delete(`/transport/admin/bookings/${bookingId}/`),
  restoreBooking: (bookingId) => api.post(`/transport/admin/bookings/${bookingId}/restore/`),
  resolveDispute: (disputeId, resolution) => api.post(`/admin/disputes/${disputeId}/resolve/`, resolution),
};

// Health Check
export const healthAPI = {
  check: () => api.get('/health/'),
  apiCheck: () => api.get('/api/health/'),
};

// Constants
export const constantsAPI = {
  getAll: () => api.get('/api/constants/'),
};

// Public site config, testimonials, contact
export const publicAPI = {
  getSiteConfig: () => api.get('/api/site-config/'),
  getTestimonials: () => api.get('/api/testimonials/'),
  submitContact: (data) => api.post('/api/contact/', data),
};

// ===== VEHICLES API =====
export const vehiclesAPI = {
  // Types de véhicules
  getVehicleTypes: () => api.get('/vehicles/vehicle-types/'),

  // Véhicules - CRUD
  getVehicles: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return api.get(`/vehicles/vehicles/?${queryParams.toString()}`);
  },

  getVehicleById: (id) => api.get(`/vehicles/vehicles/${id}/`),

  createVehicle: (data) => {
    console.log('🚗 Creating vehicle:', data);
    return api.post('/vehicles/vehicles/', data);
  },

  updateVehicle: (id, data) => {
    console.log(`🚗 Updating vehicle ${id}:`, data);
    // Utiliser PATCH pour une mise à jour partielle (plus flexible)
    return api.patch(`/vehicles/vehicles/${id}/`, data);
  },

  deleteVehicle: (id) => {
    console.log(`🚗 Deleting vehicle ${id}`);
    return api.delete(`/vehicles/vehicles/${id}/`);
  },

  // Véhicules par transporteur avec statistiques
  getVehiclesByTransporter: (transporterId, params = {}) => {
    const queryParams = new URLSearchParams({ transporter_id: transporterId });
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return api.get(`/vehicles/vehicles/by_transporter/?${queryParams.toString()}`);
  },

  // Statistiques des véhicules
  getVehicleStatistics: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return api.get(`/vehicles/vehicles/statistics/?${queryParams.toString()}`);
  },
};

// ===== DRIVERS API =====
export const driversAPI = {
  // Chauffeurs - CRUD
  getDrivers: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return api.get(`/vehicles/drivers/?${queryParams.toString()}`);
  },

  getDriverById: (id) => api.get(`/vehicles/drivers/${id}/`),

  createDriver: (data) => {
    console.log('👨‍💼 Creating driver:', data);
    return api.post('/vehicles/drivers/', data);
  },

  updateDriver: (id, data) => {
    console.log(`👨‍💼 Updating driver ${id}:`, data);
    return api.put(`/vehicles/drivers/${id}/`, data);
  },

  deleteDriver: (id) => {
    console.log(`👨‍💼 Deleting driver ${id}`);
    return api.delete(`/vehicles/drivers/${id}/`);
  },

  // Chauffeurs par transporteur avec statistiques
  getDriversByTransporter: (transporterId, params = {}) => {
    const queryParams = new URLSearchParams({ transporter_id: transporterId });
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return api.get(`/vehicles/drivers/by_transporter/?${queryParams.toString()}`);
  },

  // Assignation de véhicule
  assignVehicle: (driverId, vehicleId) => {
    console.log(`🚗👨‍💼 Assigning vehicle ${vehicleId} to driver ${driverId}`);
    return api.post(`/vehicles/drivers/${driverId}/assign_vehicle/`, {
      vehicle_id: vehicleId
    });
  },

  unassignVehicle: (driverId) => {
    console.log(`🚗👨‍💼 Unassigning vehicle from driver ${driverId}`);
    return api.post(`/vehicles/drivers/${driverId}/assign_vehicle/`, {
      vehicle_id: null
    });
  },

  // Statistiques des chauffeurs
  getDriverStatistics: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return api.get(`/vehicles/drivers/statistics/?${queryParams.toString()}`);
  },
};

// ===== UTILITY FUNCTIONS =====
export const apiUtils = {
  // Gestion des erreurs API
  handleApiError: (error) => {
    console.error('API Error:', error);

    if (error.response) {
      // Erreur de réponse du serveur
      const { status, data } = error.response;

      switch (status) {
        case 400:
          return {
            type: 'validation',
            message: 'Données invalides',
            details: data,
            status
          };
        case 401:
          return {
            type: 'auth',
            message: 'Non authentifié',
            details: data,
            status
          };
        case 403:
          return {
            type: 'permission',
            message: 'Accès refusé',
            details: data,
            status
          };
        case 404:
          return {
            type: 'not_found',
            message: 'Ressource non trouvée',
            details: data,
            status
          };
        case 500:
          return {
            type: 'server',
            message: 'Erreur serveur',
            details: data,
            status
          };
        default:
          return {
            type: 'unknown',
            message: 'Erreur inconnue',
            details: data,
            status
          };
      }
    } else if (error.request) {
      // Erreur de réseau
      return {
        type: 'network',
        message: 'Erreur de connexion',
        details: 'Impossible de contacter le serveur',
        status: 0
      };
    } else {
      // Autre erreur
      return {
        type: 'client',
        message: 'Erreur client',
        details: error.message,
        status: 0
      };
    }
  },

  // Validation des données avant envoi
  validateVehicleData: (data) => {
    const errors = {};

    if (!data.plate_number?.trim()) {
      errors.plate_number = 'Le numéro de plaque est requis';
    }

    if (!data.vehicle_type) {
      errors.vehicle_type = 'Le type de véhicule est requis';
    }

    if (data.year && (data.year < 1900 || data.year > new Date().getFullYear() + 1)) {
      errors.year = 'Année invalide';
    }

    if (data.capacity_volume && data.capacity_volume <= 0) {
      errors.capacity_volume = 'La capacité volumique doit être positive';
    }

    if (data.capacity_weight && data.capacity_weight <= 0) {
      errors.capacity_weight = 'La capacité de poids doit être positive';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  validateDriverData: (data) => {
    const errors = {};

    if (!data.first_name?.trim()) {
      errors.first_name = 'Le prénom est requis';
    }

    if (!data.last_name?.trim()) {
      errors.last_name = 'Le nom est requis';
    }

    if (!data.phone?.trim()) {
      errors.phone = 'Le téléphone est requis';
    }

    if (!data.license_number?.trim()) {
      errors.license_number = 'Le numéro de permis est requis';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  // Formatage des données pour l'affichage
  formatVehicleData: (vehicle) => {
    return {
      ...vehicle,
      displayName: `${vehicle.make || ''} ${vehicle.model || ''}`.trim() || 'Véhicule sans nom',
      insuranceStatus: vehicle.insurance_status || 'unknown',
      technicalStatus: vehicle.technical_control_status || 'unknown',
      statusColor: getStatusColor(vehicle.status),
      insuranceColor: getInsuranceColor(vehicle.insurance_status),
      technicalColor: getTechnicalColor(vehicle.technical_control_status),
    };
  },

  formatDriverData: (driver) => {
    return {
      ...driver,
      displayName: `${driver.first_name} ${driver.last_name}`,
      licenseStatus: driver.license_status || 'unknown',
      statusColor: getStatusColor(driver.status),
      licenseColor: getLicenseColor(driver.license_status),
    };
  },
};

// Fonctions utilitaires pour les couleurs de statut
const getStatusColor = (status) => {
  const colors = {
    'AVAILABLE': 'green',
    'IN_USE': 'blue',
    'MAINTENANCE': 'orange',
    'OUT_OF_SERVICE': 'red',
    'ACTIVE': 'green',
    'INACTIVE': 'gray',
    'PENDING': 'yellow',
  };
  return colors[status] || 'gray';
};

const getInsuranceColor = (status) => {
  const colors = {
    'valid': 'green',
    'expiring_soon': 'orange',
    'expired': 'red',
    'unknown': 'gray',
  };
  return colors[status] || 'gray';
};

const getTechnicalColor = (status) => {
  const colors = {
    'valid': 'green',
    'expiring_soon': 'orange',
    'expired': 'red',
    'unknown': 'gray',
  };
  return colors[status] || 'gray';
};

const getLicenseColor = (status) => {
  const colors = {
    'valid': 'green',
    'expiring_soon': 'orange',
    'expired': 'red',
    'unknown': 'gray',
  };
  return colors[status] || 'gray';
};

// API Transporteur
export const transporterAPI = {
  // Statistiques
  getStatistics: async () => {
    const response = await api.get('/accounts/transporter/statistics/');
    return response;
  },

  // Missions
  getMissions: async (params = {}) => {
    const response = await api.get('/transport/transporter/missions/', { params });
    return response;
  },

  startMission: async (missionId) => {
    const response = await api.post(`/transport/transporter/missions/${missionId}/start_mission/`);
    return response;
  },

  deliverMission: async (missionId) => {
    const response = await api.post(`/transport/transporter/missions/${missionId}/deliver_mission/`);
    return response;
  },

  completeMission: async (missionId) => {
    const response = await api.post(`/transport/transporter/missions/${missionId}/complete_mission/`);
    return response;
  },

  updateBookingStatus: async (missionId, statusCode) => {
    const response = await api.post(`/transport/transporter/missions/${missionId}/update_status/`, {
      status_code: statusCode
    });
    return response;
  },

  getMissionStatistics: async () => {
    const response = await api.get('/transport/transporter/missions/statistics/');
    return response;
  },

  // Demandes disponibles
  getAvailableRequests: async (params = {}) => {
    const response = await api.get('/transport/transporter/available-requests/', { params });
    return response;
  },

  makeOffer: async (requestId, offerData) => {
    const response = await api.post(`/transport/transporter/available-requests/${requestId}/make_offer/`, offerData);
    return response;
  },

  // Offres
  getOffers: async (params = {}) => {
    const response = await api.get('/transport/offers/', { params });
    return response;
  },

  getOfferById: async (offerId) => {
    const response = await api.get(`/transport/offers/${offerId}/`);
    return response;
  },

  // Bookings (Missions - offres acceptées)
  getBookings: async (params = {}) => {
    const response = await api.get('/transport/bookings/', { params });
    return response;
  },

  getBookingById: async (bookingId) => {
    const response = await api.get(`/transport/bookings/${bookingId}/`);
    return response;
  },

  // Véhicules
  getVehicles: async (params = {}) => {
    const response = await api.get('/transport/transporter/vehicles/', { params });
    return response;
  },
  getVehicleTypes: async () => {
    const response = await api.get('/vehicles/vehicle-types/');
    return response;
  },

  createVehicle: async (vehicleData) => {
    // L'intercepteur axios gère automatiquement FormData
    const response = await api.post('/transport/transporter/vehicles/', vehicleData);
    return response;
  },

  updateVehicle: async (vehicleId, vehicleData) => {
    // L'intercepteur axios gère automatiquement FormData
    const response = await api.patch(`/transport/transporter/vehicles/${vehicleId}/`, vehicleData);
    return response;
  },

  deleteVehicle: async (vehicleId) => {
    const response = await api.delete(`/transport/transporter/vehicles/${vehicleId}/`);
    return response;
  },

  toggleVehicleAvailability: async (vehicleId) => {
    const response = await api.post(`/transport/transporter/vehicles/${vehicleId}/toggle_availability/`);
    return response;
  },

  updateVehicleLocation: async (vehicleId, locationData) => {
    const response = await api.post(`/transport/transporter/vehicles/${vehicleId}/update_location/`, locationData);
    return response;
  },

  // Chauffeurs
  getDrivers: async (params = {}) => {
    const response = await api.get('/transport/transporter/drivers/', { params });
    return response;
  },

  createDriver: async (driverData) => {
    const response = await api.post('/transport/transporter/drivers/', driverData);
    return response;
  },

  updateDriver: async (driverId, driverData) => {
    const response = await api.patch(`/transport/transporter/drivers/${driverId}/`, driverData);
    return response;
  },

  deleteDriver: async (driverId) => {
    const response = await api.delete(`/transport/transporter/drivers/${driverId}/`);
    return response;
  },

  assignVehicleToDriver: async (driverId, vehicleId) => {
    const response = await api.post(`/transport/transporter/drivers/${driverId}/assign_vehicle/`, { vehicle_id: vehicleId });
    return response;
  },

  unassignVehicleFromDriver: async (driverId) => {
    const response = await api.post(`/transport/transporter/drivers/${driverId}/unassign_vehicle/`);
    return response;
  },

  // Paiements
  getPayments: async (params = {}) => {
    const response = await api.get('/payments/transporter/payments/', { params });
    return response;
  },

  getPaymentStatistics: async () => {
    const response = await api.get('/payments/transporter/payments/statistics/');
    return response;
  },

  getMonthlyBreakdown: async (months = 12) => {
    const response = await api.get('/payments/transporter/payments/monthly_breakdown/', {
      params: { months }
    });
    return response;
  },

  getPendingPayments: async () => {
    const response = await api.get('/payments/transporter/payments/pending_payments/');
    return response;
  },

  // Factures
  getInvoices: async (params = {}) => {
    const response = await api.get('/payments/transporter/invoices/', { params });
    return response;
  },

  getInvoiceStatistics: async () => {
    const response = await api.get('/payments/transporter/invoices/statistics/');
    return response;
  },
};

export default api;
export { api, fetchCsrfToken };
